import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoxProps } from '@mui/material';
import { Box } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';

import type { DashboardShaping, DashboardVariable } from 'src/types/dashboard';
import useMultiSelect from 'src/hooks/useMultiSelect';
import { useFilterContext } from 'src/components/app/context/FilterContext';
import {
  AccordionItem,
  AccordionItemHeader,
  AccordionItemPanel,
} from 'src/components/common/Accordion';
import ShapingFilter from 'src/components/dashboard/DashboardPage/components/VariableAccordionItem/ShapingFilter';

import { styles } from './index.styles';

export const VARIABLE_ACCORDION_ITEM_CHECKBOX_TEST_ID =
  'variable-accordion-item-checkbox-test-id';

export interface VariableAccordionItemProps extends BoxProps {
  variable: DashboardVariable;
  nested?: boolean;
}

interface ShapingNodeProps {
  label: string;
  value: string;
  showCheckbox: boolean;
}

const VariableAccordionItem: FC<VariableAccordionItemProps> = ({
  variable: { variable_id: id, name, shapings, short_name },
  sx = [],
  nested,
  ...props
}) => {
  const { t } = useTranslation(['common']);
  const shapingToNode = (shaping: DashboardShaping) => ({
    label: shaping.name,
    value: shaping.shaping_id,
    showCheckbox: true,
  });

  const { filterGroups, removeFilter, addFilter, setFilter } =
    useFilterContext();
  const ctrlDown = useMultiSelect();
  const [searchString, setSearchString] = useState<string>('');
  const [nodes, setNodes] = useState<ShapingNodeProps[]>(
    shapings.map(shapingToNode),
  );

  useEffect(() => {
    const searchResultShapings = shapings.filter((shaping) =>
      shaping.name.toLowerCase().includes(searchString.toLowerCase()),
    );
    setNodes(searchResultShapings.map(shapingToNode));
  }, [shapings, searchString]);

  const selected: string[] = useMemo(
    () =>
      filterGroups[id]?.map((filterValue) => filterValue.filterExpr[id]) || [],
    [filterGroups, id],
  );

  const handleChecked = useCallback(
    (itemId: string, isSelected: boolean) => {
      const node = nodes.find(({ value }) => String(value) === itemId);
      if (!node) return;

      const label = `${short_name}: ${node.label}`;
      if (!isSelected) {
        removeFilter(id, label);
      } else if (isSelected && !ctrlDown) {
        setFilter(id, [
          {
            label,
            filterExpr: { [id]: node.value.toString() },
          },
        ]);
      } else {
        addFilter(id, {
          label,
          filterExpr: { [id]: node.value.toString() },
        });
      }
    },
    [addFilter, ctrlDown, id, nodes, removeFilter, setFilter, short_name],
  );

  return (
    <AccordionItem
      itemName={`dashboard-variable-${id}`}
      className="VariableAccordionItem-root"
      sx={[...(Array.isArray(sx) ? sx : [sx])].filter(Boolean)}
      nested={nested}
      {...props}
    >
      <AccordionItemHeader headline={name} nested={nested} />
      <AccordionItemPanel sx={styles.accordionItemPanel}>
        <SimpleTreeView
          checkboxSelection
          multiSelect
          selectedItems={selected}
          onItemSelectionToggle={(_, itemId, isSelected) => {
            handleChecked(itemId, isSelected);
          }}
        >
          <Box>
            <ShapingFilter
              variableName={name}
              searchString={searchString}
              setSearchString={setSearchString}
            />
            {nodes && nodes.length ? (
              nodes.map(({ label, value }, i) => (
                <TreeItem
                  itemId={String(value)}
                  key={i}
                  label={label}
                  data-test-id={VARIABLE_ACCORDION_ITEM_CHECKBOX_TEST_ID}
                />
              ))
            ) : (
              <Box
                sx={{
                  py: 1,
                }}
              >
                {t('noResults')}
              </Box>
            )}
          </Box>
        </SimpleTreeView>
      </AccordionItemPanel>
    </AccordionItem>
  );
};
export default VariableAccordionItem;
